import React, { useEffect, useRef, useState } from "react";
import Button from "../../components/UI/Buttons/Button/Button";
import TextField from "../../components/TextField/TextField";
import {
  getAssociationCategories,
  updateAssociation,
} from "../../services/associationService";
import { AssociationsFetchResult } from "../../store/associationContext-actions";
import {
  ASSOCIATION_ADDRESS_MAX,
  ASSOCIATION_CITY_MAX,
  ASSOCIATION_NAME_MAX,
  ASSOCIATION_PHONE_MAX,
  AssociationCategoryResponse,
  AssociationRequest,
  AssociationResponse,
} from "../../types/association.types";
import {
  isAssociationNotAdministered,
  mapAssociationSaveError,
  shouldRefetchAssociationsAfterError,
} from "../../utils/associationErrors";
import styles from "./AssociationProfilePage.module.scss";

// Association portal, slice 2: the data form (scalars, categories,
// coordinates as plain decimals) saved through `PUT /association/{id}`.
//
// Seeding rule (same as the advisor profile page): the editor is seeded on
// mount and rebased ONLY after its own successful save. The page keys this
// card on the association id, so a switch to another association remounts it
// with that association's values by construction. A background re-read of
// `/mine` (the 30 s refresh, another card's save) never touches the editor,
// or it would delete whatever the user is halfway through typing.

export const DATA_SAVED_NOTICE = "Podaci su sačuvani.";
export const CATEGORIES_UNAVAILABLE =
  "Kategorije trenutno nisu dostupne. Sačuvane kategorije ostaju nepromenjene.";

const LATITUDE_LIMIT = 90;
const LONGITUDE_LIMIT = 180;
/** Java `Integer.MAX_VALUE`: the backend column type of `postalCode`. */
const POSTAL_CODE_MAX = 2147483647;

/** Every field as typed: the inputs are controlled strings. */
export interface DataDraft {
  name: string;
  description: string;
  address: string;
  city: string;
  postalCode: string;
  phoneNumber: string;
  latitude: string;
  longitude: string;
}

type DataErrors = Partial<Record<keyof DataDraft, string>>;

/**
 * Number fields holding text the browser could not parse. A `type="number"`
 * input then reports an EMPTY value and `validity.badInput`; the flag is the
 * only evidence, so it must block the save instead of sending null.
 */
export type BadInputFlags = Partial<Record<keyof DataDraft, boolean>>;

export const BAD_NUMBER_ERROR = "Unesite ispravan broj.";

const text = (value: string | null | undefined): string => value ?? "";
const numberText = (value: number | null | undefined): string =>
  value == null ? "" : String(value);

export const draftFrom = (association: AssociationResponse): DataDraft => ({
  name: text(association.name),
  description: text(association.description),
  address: text(association.address),
  city: text(association.city),
  postalCode: numberText(association.postalCode),
  phoneNumber: text(association.phoneNumber),
  latitude: numberText(association.latitude),
  longitude: numberText(association.longitude),
});

const categoryIdsFrom = (association: AssociationResponse): number[] =>
  (association.categories ?? []).map((category) => category.id);

const sameDraft = (a: DataDraft, b: DataDraft): boolean =>
  (Object.keys(a) as Array<keyof DataDraft>).every((key) => a[key] === b[key]);

const sameIds = (a: number[], b: number[]): boolean =>
  a.length === b.length && a.every((id) => b.includes(id));

const isDecimal = (raw: string): boolean =>
  raw.trim() !== "" && Number.isFinite(Number(raw.trim()));

/**
 * Naziv required; the bounded text fields within the backend column limits
 * (the JSON part is parsed by hand on the server, so its bean annotations
 * never run and these checks are the only guard); postal code a non-negative
 * integer; coordinates decimals within range. Blank optional fields are fine,
 * unless the browser flagged unparseable text in them (`badInput`).
 */
export const validateDataDraft = (
  draft: DataDraft,
  badInput: BadInputFlags = {},
): DataErrors => {
  const errors: DataErrors = {};
  const name = draft.name.trim();
  if (!name) {
    errors.name = "Unesite naziv udruženja.";
  } else if (name.length > ASSOCIATION_NAME_MAX) {
    errors.name = `Najviše ${ASSOCIATION_NAME_MAX} karaktera.`;
  }
  const bounded: Array<[keyof DataDraft, number]> = [
    ["address", ASSOCIATION_ADDRESS_MAX],
    ["city", ASSOCIATION_CITY_MAX],
    ["phoneNumber", ASSOCIATION_PHONE_MAX],
  ];
  bounded.forEach(([key, max]) => {
    if (draft[key].trim().length > max) {
      errors[key] = `Najviše ${max} karaktera.`;
    }
  });
  const postalCode = draft.postalCode.trim();
  if (
    postalCode &&
    (!/^\d+$/.test(postalCode) || Number(postalCode) > POSTAL_CODE_MAX)
  ) {
    errors.postalCode = "Poštanski broj mora biti ceo broj.";
  }
  if (
    draft.latitude.trim() &&
    (!isDecimal(draft.latitude) ||
      Math.abs(Number(draft.latitude.trim())) > LATITUDE_LIMIT)
  ) {
    errors.latitude = "Geografska širina mora biti broj između -90 i 90.";
  }
  if (
    draft.longitude.trim() &&
    (!isDecimal(draft.longitude) ||
      Math.abs(Number(draft.longitude.trim())) > LONGITUDE_LIMIT)
  ) {
    errors.longitude = "Geografska dužina mora biti broj između -180 i 180.";
  }
  (Object.keys(badInput) as Array<keyof DataDraft>).forEach((key) => {
    if (badInput[key]) {
      errors[key] = BAD_NUMBER_ERROR;
    }
  });
  return errors;
};

const optionalText = (raw: string): string | null => {
  const value = raw.trim();
  return value ? value : null;
};

const optionalNumber = (raw: string): number | null => {
  const value = raw.trim();
  return value ? Number(value) : null;
};

/**
 * The data form's request. The update overwrites every scalar
 * unconditionally, so the complete set goes, blank as null. `categoryIds`
 * null leaves the stored categories alone (used when the catalog could not
 * be loaded, so the user could not have edited them). Publication, media and
 * opening hours belong to other cards and are left untouched.
 */
export const buildDataRequest = (
  draft: DataDraft,
  categoryIds: number[] | null,
): AssociationRequest => ({
  name: draft.name.trim(),
  description: optionalText(draft.description),
  address: optionalText(draft.address),
  city: optionalText(draft.city),
  postalCode: optionalNumber(draft.postalCode),
  phoneNumber: optionalText(draft.phoneNumber),
  latitude: optionalNumber(draft.latitude),
  longitude: optionalNumber(draft.longitude),
  categoryIds,
  active: null,
  updateImages: false,
  workingHours: null,
});

type CategoriesState =
  | { status: "loading" }
  | { status: "loaded"; categories: AssociationCategoryResponse[] }
  | { status: "error" };

interface Baseline {
  draft: DataDraft;
  categoryIds: number[];
}

const baselineFrom = (association: AssociationResponse): Baseline => ({
  draft: draftFrom(association),
  categoryIds: categoryIdsFrom(association),
});

export interface DataCardProps {
  association: AssociationResponse;
  /** Another write on this page is in flight: the save waits for it. */
  busy: boolean;
  onPendingChange: (pending: boolean) => void;
  onDirtyChange: (dirty: boolean) => void;
  /** The page shows the copy and re-reads `/mine`. */
  onNotAdministered: () => void;
  refetch: () => Promise<AssociationsFetchResult>;
}

type TextFieldSpec = {
  key: keyof DataDraft;
  label: string;
  type: string;
  maxLength?: number;
  step?: string;
  min?: string;
  inputMode?: "numeric" | "decimal" | "tel";
};

const TEXT_FIELDS: TextFieldSpec[] = [
  { key: "address", label: "Adresa", type: "text", maxLength: ASSOCIATION_ADDRESS_MAX },
  { key: "city", label: "Grad", type: "text", maxLength: ASSOCIATION_CITY_MAX },
  {
    key: "postalCode",
    label: "Poštanski broj",
    type: "number",
    step: "1",
    min: "0",
    inputMode: "numeric",
  },
  {
    key: "phoneNumber",
    label: "Telefon",
    type: "tel",
    maxLength: ASSOCIATION_PHONE_MAX,
    inputMode: "tel",
  },
  {
    key: "latitude",
    label: "Geografska širina",
    type: "number",
    step: "any",
    inputMode: "decimal",
  },
  {
    key: "longitude",
    label: "Geografska dužina",
    type: "number",
    step: "any",
    inputMode: "decimal",
  },
];

const DataCard: React.FC<DataCardProps> = ({
  association,
  busy,
  onPendingChange,
  onDirtyChange,
  onNotAdministered,
  refetch,
}) => {
  const [baseline, setBaseline] = useState<Baseline>(() =>
    baselineFrom(association),
  );
  const [draft, setDraft] = useState<DataDraft>(() => baseline.draft);
  const [categoryIds, setCategoryIds] = useState<number[]>(
    () => baseline.categoryIds,
  );
  const [categories, setCategories] = useState<CategoriesState>({
    status: "loading",
  });
  const [errors, setErrors] = useState<DataErrors>({});
  const [badInput, setBadInput] = useState<BadInputFlags>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Synchronous double-submit guard under the disabled button.
  const savingRef = useRef(false);

  // The category catalog, once per mount. A failure is not retried: the
  // list says the categories are unavailable and the save leaves the stored
  // ones alone (`categoryIds: null`).
  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      try {
        const list = await getAssociationCategories(controller.signal);
        if (!controller.signal.aborted) {
          setCategories({
            status: "loaded",
            categories: [...list].sort((a, b) => a.ordinal - b.ordinal),
          });
        }
      } catch {
        if (!controller.signal.aborted) {
          setCategories({ status: "error" });
        }
      }
    };
    void load();
    return () => controller.abort();
  }, []);

  const dirty =
    !sameDraft(draft, baseline.draft) ||
    !sameIds(categoryIds, baseline.categoryIds);

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  // An unmounted editor holds no edits (a switch discards them).
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  const seed = (next: AssociationResponse) => {
    const fresh = baselineFrom(next);
    setBaseline(fresh);
    setDraft(fresh.draft);
    setCategoryIds(fresh.categoryIds);
    setErrors({});
  };

  const change = (key: keyof DataDraft, value: string) => {
    setNotice(null);
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  // Read on change AND on input: React fires no change while a number
  // input's value stays "" (unparseable text typed into an empty field, or
  // such text cleared again), and the flag must follow those edits too.
  const recordBadInput = (key: keyof DataDraft, target: HTMLInputElement) => {
    const bad = target.validity?.badInput === true;
    setBadInput((prev) =>
      Boolean(prev[key]) === bad ? prev : { ...prev, [key]: bad },
    );
  };

  const toggleCategory = (id: number) => {
    setNotice(null);
    setCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((other) => other !== id) : [...prev, id],
    );
  };

  const submit = async () => {
    if (savingRef.current || busy) {
      return;
    }
    const fieldErrors = validateDataDraft(draft, badInput);
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) {
      return;
    }
    const request = buildDataRequest(
      draft,
      categories.status === "loaded" ? categoryIds : null,
    );
    savingRef.current = true;
    setSaving(true);
    onPendingChange(true);
    setSaveError(null);
    setNotice(null);
    try {
      const saved = await updateAssociation(association.id, request);
      // Post-mutation re-read through the shared thunk, so every card and the
      // header converge; then rebase the editor on what the server stored.
      // When the re-read fails, the PUT response is the same server truth.
      const result = await refetch();
      const fresh = result.ok
        ? result.associations.find((row) => row.id === association.id)
        : undefined;
      const next = fresh ?? saved;
      if (next) {
        seed(next);
      }
      setNotice(DATA_SAVED_NOTICE);
    } catch (error) {
      if (isAssociationNotAdministered(error)) {
        onNotAdministered();
        return;
      }
      // The edits stay: a 400 almost always means nothing was stored, and
      // emptying the form under the message would make the user retype it.
      setSaveError(mapAssociationSaveError(error));
      if (shouldRefetchAssociationsAfterError(error)) {
        void refetch();
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
      onPendingChange(false);
    }
  };

  const fieldId = (key: keyof DataDraft) => `association-${key}`;

  const renderError = (key: keyof DataDraft) =>
    errors[key] ? (
      <span className={styles.fieldError} role="alert">
        {errors[key]}
      </span>
    ) : null;

  const renderCategories = () => (
    <fieldset className={`${styles.categoryGroup} ${styles.fullWidth}`}>
      <legend className={styles.categoryLegend}>Kategorije</legend>
      {categories.status === "loading" && (
        <p className={styles.hint}>Učitavanje kategorija...</p>
      )}
      {categories.status === "error" && (
        <p className={styles.hint}>{CATEGORIES_UNAVAILABLE}</p>
      )}
      {categories.status === "loaded" && categories.categories.length === 0 && (
        <p className={styles.hint}>Nema dostupnih kategorija.</p>
      )}
      {categories.status === "loaded" && categories.categories.length > 0 && (
        <div className={styles.checkboxList}>
          {categories.categories.map((category) => (
            <label key={category.id} className={styles.checkboxItem}>
              <input
                type="checkbox"
                checked={categoryIds.includes(category.id)}
                disabled={saving}
                onChange={() => toggleCategory(category.id)}
              />
              <span>{category.name}</span>
            </label>
          ))}
        </div>
      )}
    </fieldset>
  );

  return (
    <section className={styles.card} aria-labelledby="association-data-title">
      <h2 id="association-data-title" className={styles.cardTitle}>
        Podaci o udruženju
      </h2>
      <div className={styles.formGrid}>
        <div className={`${styles.field} ${styles.fullWidth}`}>
          <TextField
            id={fieldId("name")}
            name={fieldId("name")}
            label="Naziv"
            wrapClass={styles.textFieldWrap}
            maxLength={ASSOCIATION_NAME_MAX}
            aria-required="true"
            value={draft.name}
            disabled={saving}
            onChange={(value: string) => change("name", value)}
          />
          {renderError("name")}
        </div>

        <div className={`${styles.field} ${styles.fullWidth}`}>
          <label className={styles.fieldLabel} htmlFor={fieldId("description")}>
            Opis
          </label>
          <textarea
            id={fieldId("description")}
            className={styles.textarea}
            rows={5}
            value={draft.description}
            disabled={saving}
            onChange={(event) => change("description", event.target.value)}
          />
        </div>

        {TEXT_FIELDS.map((field) => (
          <div key={field.key} className={styles.field}>
            <TextField
              id={fieldId(field.key)}
              name={fieldId(field.key)}
              type={field.type}
              label={field.label}
              wrapClass={styles.textFieldWrap}
              maxLength={field.maxLength}
              step={field.step}
              min={field.min}
              inputMode={field.inputMode}
              value={draft[field.key]}
              disabled={saving}
              onChange={(
                value: string,
                event: React.ChangeEvent<HTMLInputElement>,
              ) => {
                if (field.type === "number") {
                  recordBadInput(field.key, event.target);
                }
                change(field.key, value);
              }}
              onInput={
                field.type === "number"
                  ? (event: React.FormEvent<HTMLInputElement>) =>
                      recordBadInput(field.key, event.currentTarget)
                  : undefined
              }
            />
            {renderError(field.key)}
          </div>
        ))}
        <p className={`${styles.hint} ${styles.fullWidth}`}>
          Koordinate se unose kao decimalni brojevi, na primer 44.8125 i
          20.4612. Prazno polje briše vrednost.
        </p>

        {renderCategories()}
      </div>

      {saveError && (
        <p className={styles.error} role="alert">
          {saveError}
        </p>
      )}
      {notice && (
        <p className={styles.success} role="status">
          {notice}
        </p>
      )}
      <Button buttonType="blue" disabled={saving || busy} onClick={submit}>
        {saving ? "Čuvanje..." : "Sačuvaj"}
      </Button>
    </section>
  );
};

export default DataCard;
